import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Row, Col, Card, Statistic, Typography, Button, Alert, Skeleton, Tabs,
  Input, Select, Space, Tag, Empty, Badge,
} from 'antd';
import {
  ReloadOutlined, SearchOutlined, ClearOutlined, FilePdfOutlined, LinkOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { adminApi } from '../services/api';
import { Elenco, Voce } from '../components/Elenco';

const { Title, Text } = Typography;

interface BillingSummary {
  mrr: number; arr: number; active: number; trial: number;
  canceling: number; canceled: number; renewing_in_30_days: number;
}
interface Subscription {
  id: number; name: string; email: string; business_name: string;
  plan: string; status: string; amount: number; renews_at: string | null;
  stripe_customer_id: string | null; stripe_subscription_id: string | null; created_at: string;
}
interface Invoice {
  id: string; customer_id: string; customer_email: string | null; customer_name: string | null;
  amount: number; status: string; paid: boolean; created: string;
  invoice_pdf: string | null; hosted_invoice_url: string | null;
}
interface FailedPayment {
  id: string; customer_id: string; amount: number;
  failure_message: string | null; created: string;
}
interface BillingData {
  summary: BillingSummary; subscriptions: Subscription[];
  invoices: Invoice[]; failed_payments: FailedPayment[];
}

const PIANO: Record<string, { t: string; c: string }> = {
  trial:   { t: 'Trial',   c: 'purple' },
  base:    { t: 'Base',    c: 'default' },
  medium:  { t: 'Medium',  c: 'blue' },
  premium: { t: 'Premium', c: 'green' },
};
const STATO: Record<string, { t: string; c: string }> = {
  active:    { t: 'Attivo',      c: 'success' },
  inactive:  { t: 'Inattivo',    c: 'default' },
  canceling: { t: 'In disdetta', c: 'warning' },
  canceled:  { t: 'Chiuso',      c: 'default' },
  past_due:  { t: 'Non pagato',  c: 'error' },
};

const euro = (n: number) =>
  '€ ' + Number(n || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 });
const data_ = (d?: string | null) => {
  if (!d) return '—';
  const t = new Date(d);
  return isNaN(+t) ? String(d) : t.toLocaleDateString('it-IT');
};

export default function BillingPage() {
  const navigate = useNavigate();
  const [data, setData] = useState<BillingData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errore, setErrore] = useState('');
  const [search, setSearch] = useState('');
  const [plan, setPlan] = useState<string | undefined>();
  const [status, setStatus] = useState<string | undefined>();

  const load = useCallback(async () => {
    setLoading(true);
    setErrore('');
    try {
      const res = await adminApi.billing();
      setData(res.data);
    } catch (e: any) {
      setErrore(e?.response?.data?.error || 'Non è stato possibile caricare i dati di fatturazione.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const subs = (data?.subscriptions ?? []).filter(s => {
    if (plan && s.plan !== plan) return false;
    if (status && s.status !== status) return false;
    if (search) {
      const q = search.toLowerCase();
      return s.name?.toLowerCase().includes(q)
        || s.email?.toLowerCase().includes(q)
        || (s.business_name || '').toLowerCase().includes(q);
    }
    return true;
  });

  const filtriAttivi = Boolean(search || plan || status);
  const azzera = () => { setSearch(''); setPlan(undefined); setStatus(undefined); };

  // Lo scheletro riproduce la forma della pagina: riquadri in alto, tabella
  // sotto. Prima restava tutto bianco con la scritta "Caricamento...", e
  // qualunque attesa sembrava eterna.
  if (loading) {
    return (
      <>
        <Skeleton active paragraph={{ rows: 1 }} style={{ maxWidth: 320, marginBottom: 22 }} />
        <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
          {[0, 1, 2, 3].map(i => (
            <Col xs={12} xl={6} key={i}><Card><Skeleton active paragraph={{ rows: 1 }} /></Card></Col>
          ))}
        </Row>
        <Card><Skeleton active paragraph={{ rows: 8 }} /></Card>
      </>
    );
  }

  if (errore) {
    return (
      <Alert
        type="error" showIcon
        message="Caricamento non riuscito" description={errore}
        action={<Button onClick={load}>Riprova</Button>}
      />
    );
  }

  const s = data?.summary;

  const colSub: ColumnsType<Subscription> = [
    {
      title: 'Cliente', dataIndex: 'name',
      sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
      render: (_, r) => (
        <>
          <div style={{ fontWeight: 600, color: '#101527' }}>{r.name || '—'}</div>
          <Text type="secondary" style={{ fontSize: 12.5 }}>{r.business_name || r.email}</Text>
        </>
      ),
    },
    {
      title: 'Piano', dataIndex: 'plan', width: 110,
      render: p => PIANO[p] ? <Tag color={PIANO[p].c}>{PIANO[p].t}</Tag> : <Text type="secondary">—</Text>,
    },
    {
      title: 'Stato', dataIndex: 'status', width: 130,
      render: v => {
        const x = STATO[v] || { t: v || '—', c: 'default' };
        return <Tag color={x.c}>{x.t}</Tag>;
      },
    },
    {
      title: 'Importo', dataIndex: 'amount', width: 120, align: 'right',
      sorter: (a, b) => a.amount - b.amount,
      render: v => <Text strong>{euro(v)}</Text>,
    },
    {
      title: 'Rinnovo', dataIndex: 'renews_at', width: 120, responsive: ['lg'],
      render: data_,
    },
  ];

  const colInv: ColumnsType<Invoice> = [
    { title: 'Data', dataIndex: 'created', width: 120, render: data_,
      sorter: (a, b) => +new Date(a.created) - +new Date(b.created), defaultSortOrder: 'descend' },
    { title: 'Cliente', dataIndex: 'customer_name',
      render: (_, r) => r.customer_name || r.customer_email || <Text type="secondary">—</Text> },
    { title: 'Importo', dataIndex: 'amount', width: 120, align: 'right',
      render: v => <Text strong>{euro(v)}</Text> },
    {
      title: 'Stato', dataIndex: 'status', width: 120,
      render: (v, r) => r.paid || v === 'paid'
        ? <Tag color="success">Pagata</Tag>
        : <Tag color={v === 'open' ? 'warning' : 'default'}>{v === 'open' ? 'Aperta' : v || '—'}</Tag>,
    },
    {
      title: '', width: 96, align: 'right',
      render: (_, r) => (
        <Space size={4}>
          {r.invoice_pdf && (
            <Button type="text" size="small" icon={<FilePdfOutlined />}
                    href={r.invoice_pdf} target="_blank" title="Scarica il PDF" />
          )}
          {r.hosted_invoice_url && (
            <Button type="text" size="small" icon={<LinkOutlined />}
                    href={r.hosted_invoice_url} target="_blank" title="Apri su Stripe" />
          )}
        </Space>
      ),
    },
  ];

  const colFail: ColumnsType<FailedPayment> = [
    { title: 'Data', dataIndex: 'created', width: 120, render: data_ },
    { title: 'Importo', dataIndex: 'amount', width: 120, align: 'right',
      render: v => <Text strong style={{ color: '#DC3055' }}>{euro(v)}</Text> },
    { title: 'Motivo', dataIndex: 'failure_message',
      render: v => v || <Text type="secondary">non specificato</Text> },
  ];

  const nFail = data?.failed_payments?.length ?? 0;

  return (
    <>
      <div className="page-top">
        <div>
          <Title level={3} style={{ margin: 0 }}>Fatturazione</Title>
          <Text type="secondary">Abbonamenti, fatture e pagamenti non riusciti</Text>
        </div>
        <Button icon={<ReloadOutlined />} onClick={load}>Aggiorna</Button>
      </div>

      {nFail > 0 && (
        <Alert
          type="warning" showIcon style={{ marginBottom: 16 }}
          message={`${nFail} ${nFail === 1 ? 'pagamento non riuscito' : 'pagamenti non riusciti'}`}
          description="Le promozioni di questi clienti sono già state messe in pausa automaticamente."
        />
      )}

      <Row gutter={[16, 16]} style={{ marginBottom: 18 }}>
        <Col xs={12} xl={6}>
          <Card><Statistic title="Ricavi mensili" value={s?.mrr ?? 0}
                           formatter={v => euro(Number(v))} valueStyle={{ color: '#0EA26B' }} /></Card>
        </Col>
        <Col xs={12} xl={6}>
          <Card><Statistic title="Ricavi annui stimati" value={s?.arr ?? 0}
                           formatter={v => euro(Number(v))} /></Card>
        </Col>
        <Col xs={12} xl={6}>
          <Card>
            <Statistic title="Abbonamenti attivi" value={s?.active ?? 0} />
            {(s?.canceling ?? 0) > 0 && <Tag color="warning">{s?.canceling} in disdetta</Tag>}
          </Card>
        </Col>
        <Col xs={12} xl={6}>
          <Card>
            <Statistic title="In rinnovo entro 30 giorni" value={s?.renewing_in_30_days ?? 0} />
          </Card>
        </Col>
      </Row>

      <Card styles={{ body: { padding: 0 } }}>
        <Tabs
          defaultActiveKey="abb"
          tabBarStyle={{ padding: '0 20px', marginBottom: 0 }}
          items={[
            {
              key: 'abb',
              label: <>Abbonamenti <Badge count={data?.subscriptions?.length ?? 0} showZero
                        color="#E4E7F0" style={{ color: '#3B4460' }} /></>,
              children: (
                <>
                  <Space wrap size={10} style={{ padding: '14px 20px' }}>
                    <Input allowClear prefix={<SearchOutlined style={{ color: '#98A1B8' }} />}
                           placeholder="Cerca cliente…" value={search}
                           onChange={e => setSearch(e.target.value)} className="filtro filtro-largo" />
                    <Select allowClear placeholder="Tutti i piani" value={plan} onChange={setPlan}
                            className="filtro"
                            options={Object.entries(PIANO).map(([v, x]) => ({ value: v, label: x.t }))} />
                    <Select allowClear placeholder="Tutti gli stati" value={status} onChange={setStatus}
                            className="filtro"
                            options={Object.entries(STATO).map(([v, x]) => ({ value: v, label: x.t }))} />
                    {filtriAttivi && <Button icon={<ClearOutlined />} onClick={azzera}>Azzera</Button>}
                  </Space>
                  <Elenco<Subscription>
                    righe={subs} colonne={colSub} rowKey={r => r.id}
                    onRowClick={r => navigate(`/users/${r.id}`)}
                    vuoto={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description={filtriAttivi ? 'Nessun abbonamento con questi filtri' : 'Ancora nessun abbonamento'} />}
                    scheda={r => (
                      <>
                        <div className="scheda-titolo">{r.name || '—'}</div>
                        <div className="scheda-sub">{r.business_name || r.email}</div>
                        <Voce etichetta="Piano">{PIANO[r.plan] ? <Tag color={PIANO[r.plan].c}>{PIANO[r.plan].t}</Tag> : '—'}</Voce>
                        <Voce etichetta="Stato">
                          <Tag color={(STATO[r.status] || { c: 'default' }).c}>
                            {(STATO[r.status] || { t: r.status }).t}
                          </Tag>
                        </Voce>
                        <Voce etichetta="Importo">{euro(r.amount)}</Voce>
                        <Voce etichetta="Rinnovo">{data_(r.renews_at)}</Voce>
                      </>
                    )}
                  />
                </>
              ),
            },
            {
              key: 'fat',
              label: <>Fatture <Badge count={data?.invoices?.length ?? 0} showZero
                        color="#E4E7F0" style={{ color: '#3B4460' }} /></>,
              children: (
                <Elenco<Invoice>
                  righe={data?.invoices ?? []} colonne={colInv} rowKey={r => r.id}
                  vuoto={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="Nessuna fattura" />}
                  scheda={r => (
                    <>
                      <div className="scheda-titolo">{r.customer_name || r.customer_email || '—'}</div>
                      <div className="scheda-sub">{data_(r.created)}</div>
                      <Voce etichetta="Importo">{euro(r.amount)}</Voce>
                      <Voce etichetta="Stato">
                        {r.paid || r.status === 'paid'
                          ? <Tag color="success">Pagata</Tag> : <Tag>{r.status}</Tag>}
                      </Voce>
                      {r.invoice_pdf && (
                        <Voce etichetta="PDF">
                          <a href={r.invoice_pdf} target="_blank" rel="noreferrer">Scarica</a>
                        </Voce>
                      )}
                    </>
                  )}
                />
              ),
            },
            {
              key: 'ko',
              label: <>Non riusciti {nFail > 0 && <Badge count={nFail} />}</>,
              children: (
                <Elenco<FailedPayment>
                  righe={data?.failed_payments ?? []} colonne={colFail} rowKey={r => r.id}
                  vuoto={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE}
                          description="Nessun pagamento non riuscito. È la situazione che vuoi." />}
                  scheda={r => (
                    <>
                      <div className="scheda-titolo">{euro(r.amount)}</div>
                      <div className="scheda-sub">{data_(r.created)}</div>
                      <Voce etichetta="Motivo">{r.failure_message || 'non specificato'}</Voce>
                    </>
                  )}
                />
              ),
            },
          ]}
        />
      </Card>
    </>
  );
}
